import {
  cpmFrom,
  groupSums,
  metricValue,
  type MediaMetric,
  type SpendImpressions,
} from './aggregate';
import {
  bucketRows,
  OTHER_DIMENSION,
  type DimensionRow,
  type MarketDimensionRow,
} from './dimensions';
import { formatMetricValue, formatSharePercent } from './format';

export type MixMode = 'brand' | 'share';

/**
 * Stands in for a share that has no honest denominator. The backend ranks its top publishers
 * per advertiser and market-wide independently, so a publisher can hold a named column here
 * while sitting inside the market's own `Other`; and the two `Other` buckets cover different
 * sets, making their ratio a share of nothing.
 */
export const NO_SHARE = '—';

export interface MixCell {
  primary: string;
  /** Absolute value under the percentage; empty under CPM, which has no companion figure. */
  secondary: string;
  /** 0..1, drives the heat shade. */
  intensity: number;
}

export interface MixTableRow {
  advertiser: string;
  total: string;
  cells: MixCell[];
}

export interface MixTableView {
  rows: MixTableRow[];
  marketCells: MixCell[];
  marketTotal: string;
}

const EMPTY: SpendImpressions = { spend: 0, impressions: 0 };

export function buildMixTable(
  rows: DimensionRow[],
  market: MarketDimensionRow[],
  advertisers: string[],
  columns: string[],
  metric: MediaMetric,
  mode: MixMode,
): MixTableView {
  const bucketed = bucketRows(rows, columns);
  const bucketedMarket = bucketRows(market, columns);
  const byAdvertiser = groupSums(bucketed, (r) => r.advertiser);
  const byAdvertiserDimension = groupSums(bucketed, (r) => `${r.advertiser}|${r.dimension}`);
  const marketByDimension = groupSums(bucketedMarket, (r) => r.dimension);
  const marketTotal = Array.from(marketByDimension.values()).reduce((sum, t) => sum + t.spend, 0);

  const percentCell = (percent: number, absolute: number): MixCell => ({
    primary: formatSharePercent(percent),
    secondary: formatMetricValue(absolute, metric),
    intensity: percent <= 0 ? 0 : Math.min(percent, 100) / 100,
  });

  const noShareCell = (absolute: number): MixCell => ({
    primary: NO_SHARE,
    secondary: formatMetricValue(absolute, metric),
    intensity: 0,
  });

  const shareCell = (dimension: string, value: number): MixCell => {
    if (dimension === OTHER_DIMENSION) return noShareCell(value);
    const marketTotals = marketByDimension.get(dimension);
    const marketValue = marketTotals ? metricValue(marketTotals, metric) : 0;
    if (marketValue <= 0) return noShareCell(value);
    return percentCell((value / marketValue) * 100, value);
  };

  const cpmCells = (totalsFor: (dimension: string) => SpendImpressions): MixCell[] => {
    const values = columns.map((dimension) => {
      const totals = totalsFor(dimension);
      return cpmFrom(totals.spend, totals.impressions);
    });
    const max = Math.max(...values, 0);
    return values.map((value) => ({
      primary: formatMetricValue(value, 'cpm'),
      secondary: '',
      intensity: max > 0 ? value / max : 0,
    }));
  };

  const tableRows = advertisers.map((advertiser) => {
    const own = byAdvertiser.get(advertiser) ?? EMPTY;
    const cellTotals = (dimension: string) =>
      byAdvertiserDimension.get(`${advertiser}|${dimension}`) ?? EMPTY;

    if (metric === 'cpm') {
      return {
        advertiser,
        total: formatMetricValue(cpmFrom(own.spend, own.impressions), 'cpm'),
        cells: cpmCells(cellTotals),
      };
    }

    const ownTotal = metricValue(own, metric);
    const cells = columns.map((dimension) => {
      const totals = cellTotals(dimension);
      const value = metricValue(totals, metric);
      if (mode === 'share') return shareCell(dimension, value);
      return percentCell(ownTotal > 0 ? (value / ownTotal) * 100 : 0, value);
    });

    return { advertiser, total: formatMetricValue(ownTotal, metric), cells };
  });

  if (metric === 'cpm') {
    return {
      rows: tableRows,
      marketCells: cpmCells((dimension) => marketByDimension.get(dimension) ?? EMPTY),
      marketTotal: formatMetricValue(
        cpmFrom(
          Array.from(marketByDimension.values()).reduce((s, t) => s + t.spend, 0),
          Array.from(marketByDimension.values()).reduce((s, t) => s + t.impressions, 0),
        ),
        'cpm',
      ),
    };
  }

  const marketMetricTotal = metricValue(
    {
      spend: marketTotal,
      impressions: Array.from(marketByDimension.values()).reduce((s, t) => s + t.impressions, 0),
    },
    metric,
  );

  const marketCells = columns.map((dimension) => {
    const marketValue = metricValue(marketByDimension.get(dimension) ?? EMPTY, metric);
    if (mode === 'share') {
      // How much of this dimension the current selection accounts for.
      const selected = advertisers.reduce(
        (sum, advertiser) =>
          sum +
          metricValue(byAdvertiserDimension.get(`${advertiser}|${dimension}`) ?? EMPTY, metric),
        0,
      );
      return shareCell(dimension, selected);
    }
    // The market's own composition, as the benchmark a brand's mix is read against.
    return percentCell(
      marketMetricTotal > 0 ? (marketValue / marketMetricTotal) * 100 : 0,
      marketValue,
    );
  });

  return {
    rows: tableRows,
    marketCells,
    marketTotal: formatMetricValue(marketMetricTotal, metric),
  };
}
